import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SEO = ({ titleOverride, descriptionOverride }) => {
  const { currentLang, t } = useLanguage();
  const location = useLocation();

  const baseUrl = 'https://bookmein.events';
  
  // Calculate inner path (without language slug)
  const pathParts = location.pathname.split('/');
  const firstSegment = pathParts[1];
  const isSlug = ['en', 'gr', 'de', 'es', 'fr'].includes(firstSegment?.toLowerCase());
  const innerPath = isSlug 
    ? (pathParts.slice(2).filter(Boolean).length ? '/' + pathParts.slice(2).filter(Boolean).join('/') : '')
    : (pathParts.filter(Boolean).length ? '/' + pathParts.filter(Boolean).join('/') : '');

  const canonicalUrl = `${baseUrl}/${currentLang}${innerPath}`;
  
  // Localized SEO title and description fallbacks if translation keys aren't updated yet
  const title = titleOverride || (t('seo.title') !== 'seo.title' ? t('seo.title') : 'BookMeIn.Events | AI-Powered Event Management Platform');
  const description = descriptionOverride || (t('seo.description') !== 'seo.description' ? t('seo.description') : 'Launch, market, and monetize your events with 0% fees on free tickets. AI guest assistant, interactive seating plans, sitemaps, custom theme engine, and low commission paid ticketing.');

  // Schema.org structured data JSON-LD
  const schemaOrgJSONLD = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "BookMeIn.Events",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "All",
      "url": baseUrl,
      "description": description,
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "EUR"
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "BookMeIn.Events",
      "url": baseUrl,
      "logo": `${baseUrl}/logo.png`,
      "sameAs": [
        "https://twitter.com/bookmeinevents",
        "https://www.linkedin.com/company/bookmein-events"
      ]
    }
  ];

  return (
    <Helmet>
      {/* HTML Language tag dynamic update */}
      <html lang={currentLang === 'gr' ? 'el' : currentLang} />

      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />

      {/* Canonical & Hreflang Alternates */}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" href={`${baseUrl}/en${innerPath}`} hreflang="en" />
      <link rel="alternate" href={`${baseUrl}/de${innerPath}`} hreflang="de" />
      <link rel="alternate" href={`${baseUrl}/gr${innerPath}`} hreflang="el" />
      <link rel="alternate" href={`${baseUrl}/es${innerPath}`} hreflang="es" />
      <link rel="alternate" href={`${baseUrl}/fr${innerPath}`} hreflang="fr" />
      <link rel="alternate" href={`${baseUrl}/en${innerPath}`} hreflang="x-default" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${baseUrl}/og-image.jpg`} />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={`${baseUrl}/og-image.jpg`} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>
    </Helmet>
  );
};

export default SEO;